import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Card, Carousel } from "react-bootstrap";
import { FaTruck, FaStore, FaHeadset } from "react-icons/fa";
import { HeroSkeleton } from "../../../../components/general/skeleton/SkeletonBlock";
import "../../../../assets/css/customer/home/herosection/HeroSection.css";
import grains from "../../../../assets/images/home/subcategories/crops/grains.jpg";
import seeds from "../../../../assets/images/home/subcategories/crops/seeds.jpg";
import heroSectionImg from "../../../../assets/images/home/heroSectionImg.jpeg";
const HeroSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [cardsVisible, setCardsVisible] = useState(false);
  const [loading, setLoading] = useState(true);

  const featuresRef = useRef(null);
  const hasAnimated = useRef(false);

  // =========================
  // Loading simulation
  // =========================
  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 1200);
    return () => clearTimeout(t);
  }, []);

  // =========================
  // Scroll animation (features)
  // =========================
  useEffect(() => {
    if (loading) return;

    const node = featuresRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          setCardsVisible(true);
        }
      },
      {
        threshold: 0.2,
      },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [loading]);

  const slides = [
    {
      id: 1,
      image: grains,
      heading: "From Farms",
      subHeading: "To Market",
      subtitle: "Bringing Farmers & Customers Together",
    },
    {
      id: 2,
      image: seeds,
      heading: "Better Seeds",
      subHeading: "Better Crops",
      subtitle: "Quality Farming Products at Your Reach",
    },
    {
      id: 3,
      image: heroSectionImg,
      heading: "Direct",
      subHeading: "Connections",
      subtitle: "Buy & Sell Agricultural Products Easily",
    },
  ];

  const features = [
    {
      id: 1,
      icon: <FaTruck />,
      title: "Fast Delivery",
      description: "Fast and reliable delivery",
    },
    {
      id: 2,
      icon: <FaStore />,
      title: "Direct From Farmers",
      description: "Buy agricultural products directly",
    },
    {
      id: 3,
      icon: <FaHeadset />,
      title: "Online Support",
      description: "Get help from our support team",
    },
  ];

  const cardDirections = ["from-left", "from-center", "from-right"];

  const handleSelect = (selectedIndex) => {
    setActiveIndex(selectedIndex);
  };

  if (loading) return <HeroSkeleton />;

  return (
    <>
      {/* ================= HERO ================= */}
      <section className="ks-hero-section">
        <Carousel
          activeIndex={activeIndex}
          onSelect={handleSelect}
          controls={false}
          indicators={false}
          interval={4000}
          pause={false}
          fade
          className="ks-hero-slider"
        >
          {slides.map((slide) => (
            <Carousel.Item key={slide.id}>
              <div
                className="ks-hero-bg-image"
                style={{ backgroundImage: `url(${slide.image})` }}
              />
            </Carousel.Item>
          ))}
        </Carousel>

        <div className="ks-hero-dark-overlay"></div>

        {/* TEXT */}
        <Container className="ks-hero-text-wrapper">
          <Row className="justify-content-center text-center">
            <Col lg={10} xl={8}>
              <div className="ks-hero-text-content">
                <h1
                  className="ks-hero-main-heading"
                  key={`heading-${activeIndex}`}
                >
                  {slides[activeIndex].heading}
                  <br />
                  <span className="ks-hero-sub-heading">
                    {slides[activeIndex].subHeading}
                  </span>
                </h1>

                {/* ✅ NEW SUBTITLE */}
                <p className="ks-hero-subtitle">
                  {slides[activeIndex].subtitle}
                </p>
              </div>
            </Col>
          </Row>
        </Container>

        {/* Bottom Curve */}
        <div className="ks-hero-bottom-curve">
          <svg viewBox="0 0 1200 150" preserveAspectRatio="none">
            <path
              d="M0,150 L0,40 Q600,150 1200,40 L1200,150 Z"
              fill="var(--bg-white)"
            />
          </svg>
        </div>
      </section>

      {/* ================= FEATURES ================= */}
      <div className="ks-features-container" ref={featuresRef}>
        <Container>
          <Row className="g-4 justify-content-center">
            {features.map((feature, index) => (
              <Col key={feature.id} xs={12} sm={6} lg={4}>
                <Card
                  className={`ks-feature-box ks-feature-box--${cardDirections[index]} ${
                    cardsVisible ? "ks-feature-box--visible" : ""
                  }`}
                  style={{
                    transitionDelay: cardsVisible ? `${index * 0.15}s` : "0s",
                  }}
                >
                  <Card.Body className="ks-feature-content">
                    <div className="ks-feature-icon-holder">
                      <div className="ks-feature-icon-circle">
                        {feature.icon}
                      </div>
                    </div>

                    <div className="ks-feature-info">
                      <h4 className="ks-feature-heading">{feature.title}</h4>
                      <p className="ks-feature-text">{feature.description}</p>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </div>
    </>
  );
};

export default HeroSection;
